import { FieldData, NDVIData, WeatherForecast, YieldPrediction, SatelliteMetadata } from './types';

// Cache for satellite data to reduce API calls
const dataCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 3600000; // 1 hour

class SatelliteDataService {
  private apiKey: string | null = null;

  setApiKey(key: string) {
    this.apiKey = key;
  }

  private getCacheKey(type: string, params: any): string {
    return `${type}_${JSON.stringify(params)}`;
  }

  private getFromCache<T>(key: string): T | null {
    const cached = dataCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data as T;
    }
    return null;
  }

  private setCache(key: string, data: any) {
    dataCache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Fetch NDVI data for a field over a date range
   * Note: In production, this would call Sentinel/Copernicus API
   */
  async fetchNDVIData(
    field: FieldData,
    startDate: string,
    endDate: string
  ): Promise<NDVIData[]> {
    const cacheKey = this.getCacheKey('ndvi', { field: field.id, startDate, endDate });
    const cached = this.getFromCache<NDVIData[]>(cacheKey);
    if (cached) return cached;

    try {
      // Simulate API call - In production, replace with actual Sentinel API
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Generate mock data
      const data = this.generateMockNDVIData(startDate, endDate);
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Failed to fetch NDVI data:', error);
      
      // Fallback to interpolated data
      return this.interpolateNDVIData(field, startDate, endDate);
    }
  }

  /**
   * Fetch weather forecast for a field location
   */
  async fetchWeatherForecast(field: FieldData): Promise<WeatherForecast[]> {
    const cacheKey = this.getCacheKey('weather', { lat: field.lat, lon: field.lon });
    const cached = this.getFromCache<WeatherForecast[]>(cacheKey);
    if (cached) return cached;

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 800));

      const data = this.generateMockWeatherData();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Failed to fetch weather data:', error);
      return [];
    }
  }

  /**
   * Calculate yield prediction based on satellite data
   */
  async calculateYieldPrediction(
    field: FieldData,
    ndviData: NDVIData[]
  ): Promise<YieldPrediction> {
    const cacheKey = this.getCacheKey('yield', { field: field.id, dataLength: ndviData.length });
    const cached = this.getFromCache<YieldPrediction>(cacheKey);
    if (cached) return cached;

    try {
      // Simulate complex calculation
      await new Promise((resolve) => setTimeout(resolve, 500));

      const avgNDVI = ndviData.reduce((sum, d) => sum + d.value, 0) / ndviData.length;
      const baseYield = 5000; // kg/hectare
      const yieldMultiplier = 1 + (avgNDVI - 0.5) * 0.8;
      const expected = baseYield * yieldMultiplier;

      const prediction: YieldPrediction = {
        expected: Math.round(expected),
        confidenceMin: Math.round(expected * 0.85),
        confidenceMax: Math.round(expected * 1.15),
        confidence: 75,
        factors: [
          {
            name: 'Vegetation Health',
            impact: avgNDVI > 0.6 ? 15 : -10,
            description: avgNDVI > 0.6 ? 'Above average health' : 'Below average health',
          },
          {
            name: 'Weather Conditions',
            impact: 5,
            description: 'Favorable rainfall pattern',
          },
          {
            name: 'Growth Stage',
            impact: 10,
            description: 'Critical development period',
          },
        ],
        historicalAverage: 4800,
        marketPrice: 2.5, // per kg
      };

      this.setCache(cacheKey, prediction);
      return prediction;
    } catch (error) {
      console.error('Failed to calculate yield prediction:', error);
      throw error;
    }
  }

  /**
   * Batch fetch data for multiple fields
   */
  async batchFetchNDVI(
    fields: FieldData[],
    startDate: string,
    endDate: string
  ): Promise<Map<string, NDVIData[]>> {
    const results = new Map<string, NDVIData[]>();

    // Process in batches of 5 to avoid overwhelming the API
    const batchSize = 5;
    for (let i = 0; i < fields.length; i += batchSize) {
      const batch = fields.slice(i, i + batchSize);
      const promises = batch.map((field) =>
        this.fetchNDVIData(field, startDate, endDate).then((data) => ({
          fieldId: field.id,
          data,
        }))
      );

      const batchResults = await Promise.all(promises);
      batchResults.forEach(({ fieldId, data }) => {
        results.set(fieldId, data);
      });
    }

    return results;
  }

  // Helper methods for mock data generation

  private generateMockNDVIData(startDate: string, endDate: string): NDVIData[] {
    const data: NDVIData[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    // Generate data every 5 days (simulating satellite revisit time)
    for (let i = 0; i <= days; i += 5) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);

      // Simulate growth curve
      const progress = i / days;
      const growthFactor = Math.sin(progress * Math.PI);
      const ndviValue = 0.3 + growthFactor * 0.5 + (Math.random() - 0.5) * 0.1;
      const cloudCover = Math.random() * 40;

      data.push({
        date: date.toISOString().split('T')[0],
        value: Math.max(0, Math.min(1, ndviValue)),
        cloudCover,
        quality: cloudCover < 20 ? 'high' : cloudCover < 40 ? 'medium' : 'low',
      });
    }

    return data;
  }

  private generateMockWeatherData(): WeatherForecast[] {
    const forecasts: WeatherForecast[] = [];
    const today = new Date();

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);

      const temp = 20 + Math.random() * 15;
      const precip = Math.random() < 0.3 ? Math.random() * 20 : 0;

      forecasts.push({
        date: date.toISOString().split('T')[0],
        tempMin: Math.round(temp - 5),
        tempMax: Math.round(temp + 5),
        precipitation: Math.round(precip * 10) / 10,
        humidity: Math.round(50 + Math.random() * 40),
        windSpeed: Math.round(Math.random() * 20),
        conditions: precip > 0 ? 'Rainy' : 'Clear',
        advisory: i === 2 && precip === 0 ? 'Good conditions for fertilizing' : undefined,
      });
    }

    return forecasts;
  }

  private interpolateNDVIData(
    field: FieldData,
    startDate: string,
    endDate: string
  ): NDVIData[] {
    // Fallback interpolation when satellite data is unavailable
    return this.generateMockNDVIData(startDate, endDate);
  }
}

export const satelliteApi = new SatelliteDataService();
